package sarka.design.skoria.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.async
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.selects.select
import sarka.design.skoria.network.AuthRepository
import sarka.design.skoria.network.AuthResult

data class LoginUiState(
    val username: String = "",
    val password: String = "",
    val isLoading: Boolean = false,
    val error: String? = null,
    val isLoggedIn: Boolean = false,
    val needsEmailVerification: Boolean = false
)

class LoginViewModel(
    private val authRepository: AuthRepository = AuthRepository()
) : ViewModel() {

    private val _state = MutableStateFlow(LoginUiState())
    val state: StateFlow<LoginUiState> = _state

    companion object {
        private const val REQUEST_TIMEOUT_MS = 10_000L
    }

    fun onUsernameChange(value: String) {
        _state.value = _state.value.copy(username = value, error = null)
    }

    fun onPasswordChange(value: String) {
        _state.value = _state.value.copy(password = value, error = null)
    }

    fun login() {
        val current = _state.value
        if (current.isLoading) return
        if (current.username.isBlank() || current.password.isBlank()) {
            _state.value = current.copy(error = "Username and password are required")
            return
        }

        _state.value = current.copy(isLoading = true, error = null, needsEmailVerification = false)

        viewModelScope.launch {
            val result = performWithTimeout {
                authRepository.login(current.username, current.password)
            }
            _state.value = when (result) {
                is AuthResult.Success -> _state.value.copy(isLoading = false, isLoggedIn = true)
                is AuthResult.Error -> {
                    val needsVerification = result.message.contains("verify your email", ignoreCase = true)
                    _state.value.copy(
                        isLoading = false, 
                        error = result.message,
                        needsEmailVerification = needsVerification
                    )
                }
            }
        }
    }

    private suspend fun performWithTimeout(block: suspend () -> AuthResult): AuthResult {
        return try {
            kotlinx.coroutines.coroutineScope {
                val request = async { block() }
                val timeout = async {
                    delay(REQUEST_TIMEOUT_MS)
                    AuthResult.Error("Request timed out. Check your connection and try again.")
                }

                select {
                    request.onAwait { result ->
                        timeout.cancel()
                        result
                    }
                    timeout.onAwait { result ->
                        request.cancel()
                        result
                    }
                }
            }
        } catch (e: Exception) {
            AuthResult.Error(e.message ?: "Something went wrong")
        }
    }
}
